import React from "react";
import dayjs from "dayjs";
import {SolutionOutlined} from "@ant-design/icons";
import {DatePicker, DatePickerSizes, Input, InputNumber, InputSizes, Select, Text, TextAs, TextSizes, SelectSizes} from "@clockout-dev/design-elements";
import ComposerSection from "./ComposerSection";
import {COMPENSATION_UNIT_OPTIONS, EMPLOYMENT_STATUS_OPTIONS, PAY_FREQUENCY_OPTIONS} from "../uploads-simulator.constants";
import styles from "../styles/sor-composer.module.scss";

/**
 * SOR employment + income section. `compensation_unit: "annually"` is the
 * lever for the salaried-worker-no-shifts-required rejection demo; amounts
 * are authored in DOLLARS (the service normalizes to cents).
 *
 * @param {Object} props
 * @param {Object} props.employment — `sor.employment`
 * @param {Object} props.income — `sor.income`
 * @param {Record<string, string>} [props.errors]
 * @param {boolean} [props.filled]
 * @param {(employment: Object) => void} props.onEmploymentChange
 * @param {(income: Object) => void} props.onIncomeChange
 */
function SorEmploymentIncomeSection({employment, income, errors, filled, onEmploymentChange, onIncomeChange}) {
    const employmentValue = employment || {};
    const incomeValue = income || {};
    const setEmployment = (field, value) => onEmploymentChange({...employmentValue, [field]: value});
    const setIncome = (field, value) => onIncomeChange({...incomeValue, [field]: value});

    return (
        <ComposerSection icon={<SolutionOutlined />} title="Employment & Income" filled={filled}>
            <div className={styles.fieldGrid}>
                <Input
                    label="Employer name"
                    size={InputSizes.SMALL}
                    placeholder="Acme Logistics"
                    value={employmentValue.employer_name || ""}
                    onChange={event => setEmployment("employer_name", event.target.value)}
                />
                <Select
                    size={SelectSizes.SMALL}
                    label="Employment status"
                    allowClear
                    options={EMPLOYMENT_STATUS_OPTIONS}
                    value={employmentValue.status || undefined}
                    onChange={status => setEmployment("status", status || "")}
                />
                <Input label="Job title" size={InputSizes.SMALL} value={employmentValue.title || ""} onChange={event => setEmployment("title", event.target.value)} />
                <DatePicker
                    label="Start date"
                    size={DatePickerSizes.SMALL}
                    className={styles.dateField}
                    value={employmentValue.start_date ? dayjs(employmentValue.start_date) : null}
                    onChange={date => setEmployment("start_date", date ? date.format("YYYY-MM-DD") : "")}
                />
            </div>

            <div className={styles.fieldGrid}>
                <Select
                    size={SelectSizes.SMALL}
                    label="Worker unit"
                    options={COMPENSATION_UNIT_OPTIONS}
                    value={incomeValue.compensation_unit || "hourly"}
                    onChange={unit => setIncome("compensation_unit", unit)}
                />
                <InputNumber
                    label="Compensation amount ($)"
                    size={InputSizes.SMALL}
                    className={styles.numberField}
                    min={0}
                    status={errors?.["income.compensation_amount"] ? "error" : ""}
                    value={incomeValue.compensation_amount}
                    onChange={amount => setIncome("compensation_amount", amount)}
                />
                <InputNumber
                    label="Hourly wage ($)"
                    size={InputSizes.SMALL}
                    className={styles.numberField}
                    min={0}
                    status={errors?.["income.hourly_wage"] ? "error" : ""}
                    value={incomeValue.hourly_wage}
                    onChange={wage => setIncome("hourly_wage", wage)}
                />
                <Select
                    size={SelectSizes.SMALL}
                    label="Pay frequency"
                    allowClear
                    options={PAY_FREQUENCY_OPTIONS}
                    value={incomeValue.pay_frequency || undefined}
                    onChange={frequency => setIncome("pay_frequency", frequency || "")}
                />
                <Input label="Currency" size={InputSizes.SMALL} value={incomeValue.currency || ""} onChange={event => setIncome("currency", event.target.value.toUpperCase())} />
                <Input label="Income employer (if different)" size={InputSizes.SMALL} value={incomeValue.employer_name || ""} onChange={event => setIncome("employer_name", event.target.value)} />
            </div>

            <Text as={TextAs.P} size={TextSizes.EXTRA_SMALL} className={styles.sectionHint}>
                Pick "Annually (salaried)" to demo the salaried-worker rejection on the Shifts tab. All money is in dollars.
            </Text>
        </ComposerSection>
    );
}

export default SorEmploymentIncomeSection;
